// src/features/channels/useGuildSettings.ts
// Read side deliberately has no separate query here — GuildContext
// already fetches every owned guild's settings via GET /api/guilds,
// so this page reads directly from useGuild()'s guilds array rather
// than duplicating the fetch. Only the PATCH (write) side needs its
// own hook, and its success invalidates the SAME ['guilds'] query key
// GuildContext uses internally, so both stay in sync automatically.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';

// Only the channel-related subset of guilds.js's SETTINGS_SCHEMA —
// prefix/modRoleId/cooldownOverrides belong to a general Settings
// page (or Commands, for cooldowns), not here.
export interface ChannelSettings {
  logChannelId?: string | null;
  welcomeChannelId?: string | null;
  welcomeMessage?: string;
  leaveMessage?: string;
  blacklistedChannels?: string[];
}

export function useUpdateGuildSettings(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: ChannelSettings) =>
      apiFetch(`/api/guilds/${guildId}`, {
        method: 'PATCH',
        body: JSON.stringify({ settings }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['guilds'] }),
  });
}
